import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { entryGroupsQueryKey } from "@/shared/cache";
import { useApi } from "@/shared/utils/request";
import {
	emptyEntryUndoHistory,
	MAX_ENTRY_UNDO_RECORDS,
	normalizeEntryUndoHistory,
	readEntryUndoHistory,
	updateEntryUndoHistory,
} from "./storage";
import {
	ENTRIES_UNDO_SCHEMA_VERSION,
	type EntryRestoreResponse,
	type EntryUndoHistory,
	type EntryUndoMetadata,
	type EntryUndoRecord,
	findEntryUndoMetadata,
	isEntryRestoreResponse,
} from "./types";
import {
	createEntryUndoHistorySync,
	type EntryUndoHistorySync,
	withEntryUndoLock,
} from "./crossTab";

type EntriesUndoContextValue = {
	canUndo: boolean;
	undoLabel: string | null;
	isUndoing: boolean;
	isMutating: boolean;
	invalidateExportedGroups: (entryGroupIds: string[]) => Promise<void>;
	undo: () => Promise<void>;
	runEntryMutation: <T>(mutation: () => Promise<T>) => Promise<T>;
};

const EntriesUndoContext = createContext<EntriesUndoContextValue | null>(null);

function newRecord(metadata: EntryUndoMetadata): EntryUndoRecord {
	return {
		...metadata,
		id: crypto.randomUUID(),
		createdAt: new Date().toISOString(),
	};
}

function overlaps(
	record: EntryUndoRecord,
	groupIds: Set<string>,
	entryIds: Set<string> = new Set(),
): boolean {
	return (
		record.affectedEntryGroupIds.some((id) => groupIds.has(id)) ||
		record.affectedEntryIds.some((id) => entryIds.has(id))
	);
}

export function EntriesUndoProvider({ children }: { children: ReactNode }) {
	const { user, organizationId } = useAuth();
	const { makeApiRequest } = useApi();
	const queryClient = useQueryClient();
	const scope = user?.id
		? `${organizationId ?? window.location.hostname}:${user.id}`
		: "";
	const [history, setHistory] = useState<EntryUndoHistory>(() =>
		normalizeEntryUndoHistory(null),
	);
	const [loadedScope, setLoadedScope] = useState("");
	const [isUndoing, setIsUndoing] = useState(false);
	const [pendingMutationCount, setPendingMutationCount] = useState(0);
	const mutationQueueRef = useRef(Promise.resolve<unknown>(undefined));
	const historyQueueRef = useRef(Promise.resolve<unknown>(undefined));
	const activeScopeRef = useRef("");
	const scopeEpochRef = useRef(0);
	const undoInFlightRef = useRef(false);
	const historySyncRef = useRef<EntryUndoHistorySync | null>(null);

	useEffect(() => {
		let active = true;
		activeScopeRef.current = scope;
		const epoch = ++scopeEpochRef.current;
		setLoadedScope("");
		setHistory(emptyEntryUndoHistory());
		if (!scope)
			return () => {
				active = false;
			};
		const load = historyQueueRef.current.then(() =>
			readEntryUndoHistory(scope),
		);
		historyQueueRef.current = load.then(
			() => undefined,
			() => undefined,
		);
		void load.then((stored) => {
			if (
				!active ||
				activeScopeRef.current !== scope ||
				scopeEpochRef.current !== epoch
			)
				return;
			setHistory(stored);
			setLoadedScope(scope);
		});
		return () => {
			active = false;
		};
	}, [scope]);

	useEffect(() => {
		if (!scope) return;
		const targetScope = scope;
		const epoch = scopeEpochRef.current;
		const sync = createEntryUndoHistorySync((changedScope) => {
			if (
				changedScope !== targetScope ||
				activeScopeRef.current !== targetScope ||
				scopeEpochRef.current !== epoch
			)
				return;
			const reload = historyQueueRef.current.then(() =>
				readEntryUndoHistory(targetScope),
			);
			historyQueueRef.current = reload.then(
				() => undefined,
				() => undefined,
			);
			void reload.then((stored) => {
				if (
					activeScopeRef.current !== targetScope ||
					scopeEpochRef.current !== epoch
				)
					return;
				setHistory(stored);
				setLoadedScope(targetScope);
			});
		});
		historySyncRef.current = sync;
		return () => {
			if (historySyncRef.current === sync) historySyncRef.current = null;
			sync?.close();
		};
	}, [scope]);

	const updateHistoryUnlocked = useCallback(
		async (updater: (current: EntryUndoHistory) => EntryUndoHistory) => {
			const targetScope = scope;
			const epoch = scopeEpochRef.current;
			if (!targetScope) return;
			const update = historyQueueRef.current.then(async () => {
				if (
					activeScopeRef.current !== targetScope ||
					scopeEpochRef.current !== epoch
				)
					return;
				const next = await updateEntryUndoHistory(targetScope, updater);
				if (
					activeScopeRef.current !== targetScope ||
					scopeEpochRef.current !== epoch
				)
					return;
				setHistory(next);
				setLoadedScope(targetScope);
				historySyncRef.current?.notify(targetScope);
			});
			historyQueueRef.current = update.then(
				() => undefined,
				() => undefined,
			);
			await update;
		},
		[scope],
	);

	const updateHistory = useCallback(
		async (updater: (current: EntryUndoHistory) => EntryUndoHistory) => {
			if (!scope) return;
			await withEntryUndoLock(scope, () => updateHistoryUnlocked(updater));
		},
		[scope, updateHistoryUnlocked],
	);

	const enqueueEntryMutation = useCallback(
		<T,>(mutation: () => Promise<T>): Promise<T> => {
			setPendingMutationCount((count) => count + 1);
			const execute = () =>
				mutation().finally(() => {
					setPendingMutationCount((count) => Math.max(0, count - 1));
				});
			const run = mutationQueueRef.current.then(execute, execute);
			mutationQueueRef.current = run.then(
				() => undefined,
				() => undefined,
			);
			return run;
		},
		[],
	);

	const persistMutationResult = useCallback(
		async (result: unknown) => {
			const metadata = findEntryUndoMetadata(result);
			if (!metadata) return;
			await updateHistoryUnlocked((current) => ({
				version: ENTRIES_UNDO_SCHEMA_VERSION,
				undo: [...current.undo, newRecord(metadata)].slice(
					-MAX_ENTRY_UNDO_RECORDS,
				),
				redo: [],
			}));
		},
		[updateHistoryUnlocked],
	);

	const runEntryMutation = useCallback(
		<T,>(mutation: () => Promise<T>): Promise<T> =>
			enqueueEntryMutation(() =>
				withEntryUndoLock(scope, async () => {
					try {
						const result = await mutation();
						await persistMutationResult(result);
						return result;
					} catch (error) {
						const committedResult =
							error && typeof error === "object" && "committedResult" in error
								? (error as { committedResult?: unknown }).committedResult
								: undefined;
						if (committedResult !== undefined)
							await persistMutationResult(committedResult);
						throw error;
					}
				}),
			),
		[enqueueEntryMutation, persistMutationResult, scope],
	);

	const invalidateExportedGroups = useCallback(
		async (entryGroupIds: string[]) => {
			if (entryGroupIds.length === 0) return;
			const ids = new Set(entryGroupIds);
			await updateHistory((current) => ({
				...current,
				undo: current.undo.filter((record) => !overlaps(record, ids)),
				redo: current.redo.filter((record) => !overlaps(record, ids)),
			}));
		},
		[updateHistory],
	);

	const undo = useCallback(async () => {
		if (!scope || undoInFlightRef.current) return;
		const attempt: { record: EntryUndoRecord | null } = { record: null };
		undoInFlightRef.current = true;
		setIsUndoing(true);
		try {
			await enqueueEntryMutation(() =>
				withEntryUndoLock(scope, async () => {
					const latest = await historyQueueRef.current.then(() =>
						readEntryUndoHistory(scope),
					);
					if (activeScopeRef.current !== scope) return;
					setHistory(latest);
					setLoadedScope(scope);
					const currentRecord = latest.undo.at(-1);
					if (!currentRecord) return;
					attempt.record = currentRecord;

					const response = await makeApiRequest({
						endpoint: "/v2/entry-state/restore",
						method: "POST",
						body: { receipt: currentRecord.receipt },
					});
					if (response.status === "ERROR") {
						const error = new Error(
							response.error || "Unable to undo this action",
						);
						Object.assign(error, { code: response.apiError?.code });
						throw error;
					}

					if (!isEntryRestoreResponse(response.data)) {
						await updateHistoryUnlocked((current) => {
							if (current.undo.at(-1)?.id !== currentRecord.id) return current;
							return {
								...current,
								undo: current.undo.slice(0, -1),
								redo: [],
							};
						});
						await queryClient.invalidateQueries({
							queryKey: entryGroupsQueryKey,
						});
						throw Object.assign(
							new Error(
								"Undo completed, but the server returned an invalid receipt",
							),
							{ undoCommitted: true },
						);
					}

					const restored: EntryRestoreResponse = response.data;
					const reverse: EntryUndoRecord = {
						object: "entry_undo_metadata",
						id: crypto.randomUUID(),
						createdAt: new Date().toISOString(),
						receipt: restored.reverseReceipt,
						version: ENTRIES_UNDO_SCHEMA_VERSION,
						action: restored.action,
						label: restored.label,
						affectedEntryGroupIds: restored.affectedEntryGroupIds,
						affectedEntryIds: restored.affectedEntryIds,
					};
					await updateHistoryUnlocked((current) => {
						if (current.undo.at(-1)?.id !== currentRecord.id) return current;
						return {
							...current,
							undo: current.undo.slice(0, -1),
							redo: [...current.redo, reverse].slice(-MAX_ENTRY_UNDO_RECORDS),
						};
					});
					await queryClient.invalidateQueries({
						queryKey: entryGroupsQueryKey,
					});
					toast.success(`Undid ${currentRecord.label}`);
				}),
			);
		} catch (error) {
			const code = (error as { code?: string }).code;
			const failedRecord = attempt.record;
			if (
				failedRecord &&
				(code === "UNDO_CONFLICT" ||
					code === "UNDO_EXPORTED" ||
					code === "UNDO_FORBIDDEN" ||
					code === "UNDO_RECEIPT_INVALID" ||
					code === "UNDO_RECEIPT_VERSION_UNSUPPORTED")
			) {
				const groupIds = new Set(failedRecord.affectedEntryGroupIds);
				const entryIds = new Set(failedRecord.affectedEntryIds);
				await updateHistory((current) => ({
					...current,
					undo: current.undo.filter(
						(item) => !overlaps(item, groupIds, entryIds),
					),
					redo: current.redo.filter(
						(item) => !overlaps(item, groupIds, entryIds),
					),
				}));
				await queryClient.invalidateQueries({ queryKey: entryGroupsQueryKey });
			}
			toast.error(
				code === "UNDO_CONFLICT"
					? "This entry changed after the action and can no longer be undone"
					: error instanceof Error
						? error.message
						: "Unable to undo this action",
			);
		} finally {
			undoInFlightRef.current = false;
			setIsUndoing(false);
		}
	}, [
		enqueueEntryMutation,
		makeApiRequest,
		queryClient,
		scope,
		updateHistory,
		updateHistoryUnlocked,
	]);

	const value = useMemo<EntriesUndoContextValue>(
		() => ({
			canUndo: loadedScope === scope && history.undo.length > 0,
			undoLabel: history.undo.at(-1)?.label ?? null,
			isUndoing,
			isMutating: pendingMutationCount > 0,
			invalidateExportedGroups,
			undo,
			runEntryMutation,
		}),
		[
			history.undo,
			invalidateExportedGroups,
			isUndoing,
			pendingMutationCount,
			loadedScope,
			runEntryMutation,
			scope,
			undo,
		],
	);

	return (
		<EntriesUndoContext.Provider value={value}>
			{children}
		</EntriesUndoContext.Provider>
	);
}

export function useEntriesUndo(): EntriesUndoContextValue {
	const context = useContext(EntriesUndoContext);
	if (!context)
		throw new Error("useEntriesUndo must be used within EntriesUndoProvider");
	return context;
}
